import type { CommentAttachment, ExecEmailSource } from '../types';
import { isCsvType, isImageType, isPdfType } from './projectFileService';

/** Sentinel leadId for OPS task file viewers (annotations/markup in shared collections). */
export const OPS_VIEWER_LEAD_ID = '__ops__';

/** Stable document key for OPS comment attachments (survives Storage URL token refresh). */
export function buildOpsAttachmentDocumentId(taskId: string, attachmentId: string): string {
  return `ops:${taskId}:${attachmentId}`;
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

/** Guess MIME from filename when Gmail/Storage only provide octet-stream. */
export function inferMimeFromFilename(fileName: string): string | undefined {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.bmp')) return 'image/bmp';
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  if (lower.endsWith('.csv')) return 'text/csv';
  return undefined;
}

export function effectiveOpsAttachmentMime(att: CommentAttachment, emailSource?: ExecEmailSource | null): string {
  const name = opsAttachmentDisplayName(att, emailSource);
  if (att.type && att.type !== 'application/octet-stream') return att.type;
  return inferMimeFromFilename(name) || att.type || 'application/octet-stream';
}

export function opsAttachmentDisplayName(
  att: CommentAttachment,
  emailSource?: ExecEmailSource | null,
): string {
  if (att.name && att.name !== 'attachment') return att.name;
  if (emailSource?.filename) return emailSource.filename;
  return att.name || 'File';
}

export type OpsAttachmentViewerKind = 'pdf' | 'image' | 'csv' | 'other';

export function opsAttachmentViewerKind(
  att: CommentAttachment,
  emailSource?: ExecEmailSource | null,
): OpsAttachmentViewerKind {
  const name = opsAttachmentDisplayName(att, emailSource);
  const mime = effectiveOpsAttachmentMime(att, emailSource);
  if (isPdfType(mime) || /\.pdf$/i.test(name)) return 'pdf';
  if (isImageType(mime) || IMAGE_EXT.test(name)) return 'image';
  if (isCsvType(mime, name)) return 'csv';
  return 'other';
}

export function opsAttachmentSupportsInlineViewer(
  att: CommentAttachment,
  emailSource?: ExecEmailSource | null,
): boolean {
  return opsAttachmentViewerKind(att, emailSource) !== 'other';
}
